import {Inject, Injectable} from '@angular/core';
import { RouteNames } from '../enums';
import { NgRedux } from '@angular-redux/store';
import { UUID } from 'angular2-uuid';
import { IAppState, IAppConfig } from '../../../app/store';
import { WindowRef, getRegionfromCountryCode, MediaQueryService } from '../../common'
import { CookieService } from 'ngx-cookie-service';
import { ITrackingJson, TrackingJson } from '../interfaces';
import {useDefault} from '../../common/common';
import {Unsubscribe} from 'redux';
import {AppAction, AppActionState} from '../../store/interfaces/IAppState';
import {ActionHandler} from '../../store/action.handler';
import {EnsightenEventType} from '../../common/model/ensighten/ensighten-event-type.model';
import {APP_CONFIG} from '../config/app.config';
import {AppEnsightenEventType} from '../../common/model/ensighten/app-ensighten-event-type.model';
import {ApplicationEnsightenEvent} from '../../common/model/ensighten/application-event.model';
import {ApplicationLocale} from '../../common/model/application-locale.model';
import {SessionStorageService} from 'ngx-webstorage';
import {TrackingSession} from '../../common/model/ensighten/tracking-session.model';
import { FeatureFlags } from '../model/featureflags';
import {LogService} from './logging.service';

const GET_RESERVATION_EVENT = new ApplicationEnsightenEvent(AppEnsightenEventType.GET_RESERVATION, [AppAction.GET_RESERVATION]);
const GET_HOTEL_EVENT = new ApplicationEnsightenEvent(AppEnsightenEventType.GET_HOTEL, [AppAction.GET_HOTEL]);
const CHECK_AVAILABILITY_EVENT = new ApplicationEnsightenEvent(AppEnsightenEventType.CHECK_AVAILABILITY, [AppAction.CHECK_AVAILABILITY], EnsightenEventType.OTHER);
const MODIFY_RESERVATION_EVENT = new ApplicationEnsightenEvent(AppEnsightenEventType.MODIFY_RESERVATION, [AppAction.MODIFY_RESERVATION]);
const DEFAULT_PAGE_READY_EVENT = new ApplicationEnsightenEvent(AppEnsightenEventType.RESERVATION_READY, [AppAction.GET_RESERVATION, AppAction.GET_HOTEL], EnsightenEventType.PAGE_COMPLETE);
const GET_MEMBER_EVENT = new ApplicationEnsightenEvent(AppEnsightenEventType.MEMBER_READY, [AppAction.GET_MEMBER]);

const EVENT_TRUE = 'true';
const DEFAULT_COUNTRY_CODE = 'US';
const APP_TRACKING_UUID_NAME = 'ihgAppTrackingSession';

const CLASS_NAME = 'TrackingJsonService';

/*
  CAPTURES DATA TO SEND TO ENSIGHTEN (IT IS EASIEST IF YOUR DATA IS EITHER IN A COOKIE OR THE REDUX STORE)
    1.  IF A NEW API CALL IS ADDED TO THE APPLICATION WHICH HAS DATA NEEDING TO BE TRACKED
        ADD THE API CALL TO THE ApiCalls ENUM AND THE _listOfApiCalls ARRAY
    2.  IF A NEW ROUTE IS ADDED TO THE APPLICATION WHICH NEEDS TO TRACK ANALYTICS
        ADD THE ROUTE TO THE RouteNames ENUM
        AND IN doPageViewStart ADD ANOTHER CASE TO CAPTURE THE DATA
  THE REST WILL TAKE CARE OF ITSELF
*/
@Injectable()
export class TrackingJsonService {
  private _trackingJson: TrackingJson;
  private _reduxSubscription: Unsubscribe;
  private _reduxEventDataMapping: Map<AppAction, (state: IAppState) => ITrackingJson>;
  // List of events that can be trigered from the page to Ensighten
  private _reduxEventEnsightenTrigger: Array<ApplicationEnsightenEvent>;

  constructor(
    @Inject(APP_CONFIG) private _appConfig: IAppConfig,
    private _ngRedux: NgRedux<IAppState>,
    private _window: WindowRef,
    private _log: LogService,
    private _cookieService: CookieService,
    private _sessionStorageService: SessionStorageService,
    private _mediaQueryService: MediaQueryService
  ) {
    // Map of app events and data mapping function
    this._reduxEventDataMapping = new Map<AppAction, (app: IAppState) => ITrackingJson>([
      [AppAction.GET_RESERVATION, this.mapReservation],
      [AppAction.GET_MEMBER, this.mapMember],
      [AppAction.GET_HOTEL, this.mapHotel]
    ]);
  }

  // called when data layer is partially ready, before any rendering
  subscribeRoute(routeName: RouteNames) {
    // Reset tracking json
    this._trackingJson = new TrackingJson();

    this.initRoute(routeName);

    // Assign route data
    this.assignData({
      pageType: routeName,
      type: routeName,
      controllerName: routeName
    });
    this.loadStaticData();

    this.sendEventToEnsighten(EnsightenEventType.PAGE_START);
    this._reduxSubscription = this._ngRedux.subscribe( () => {
      this.updateFromState();
    });
    this._reduxEventEnsightenTrigger.forEach((element: ApplicationEnsightenEvent) => {
      if (element.isReady()) {
        this.handleReady(element);
      }
    });
  }
  /*
    Manually sets a page as ready
   */
  setPageReady () {
    const fakeReady = new ApplicationEnsightenEvent(AppEnsightenEventType.PAGE_READY, [], EnsightenEventType.PAGE_COMPLETE);
    this.handleReady(fakeReady);
  }
  unsubscribeRoute(routeName: RouteNames) {
    this._reduxSubscription();
  }

  private initRoute(routeName: RouteNames) {
    switch (routeName) {
      case RouteNames.CancelReservation:
        this._reduxEventEnsightenTrigger = [DEFAULT_PAGE_READY_EVENT];
        break;
      case RouteNames.CancelConfirmation:
        this._reduxEventEnsightenTrigger = [DEFAULT_PAGE_READY_EVENT];
        break;
      case RouteNames.ManageYourStay:
        this._reduxEventEnsightenTrigger = [GET_MEMBER_EVENT];
        break;
      case RouteNames.ModifyReservation:
        this._reduxEventEnsightenTrigger = [
          GET_RESERVATION_EVENT,
          GET_HOTEL_EVENT,
          CHECK_AVAILABILITY_EVENT,
          MODIFY_RESERVATION_EVENT,
          DEFAULT_PAGE_READY_EVENT];
        break;
      case RouteNames.StayPreferences:
          this._reduxEventEnsightenTrigger = [DEFAULT_PAGE_READY_EVENT];
          break;
    }
  }

  private updateFromState() {
    const state = this._ngRedux.getState();
    this._reduxEventDataMapping.forEach((mapper, key) => {
      ActionHandler.handleSuccess(state, key, () => {
        this.assignData(mapper(state));
      });
    });

    this._reduxEventEnsightenTrigger.forEach((element: ApplicationEnsightenEvent) => {
      if (element.isReady()) {
        return;
      }
      if (state.state !== AppActionState.SUCCESS) {
        return;
      }
      element.appActionReady(state.actionType);
      if (element.isReady()) {
        this.handleReady(element);
      }
    });
  }

  private handleReady(element: ApplicationEnsightenEvent) {
    this.assignData({
      eventId: element.type,
      timestamp: Date.now()
    });
    if (element.ensightenEventWhenReady) {
      this.sendEventToEnsighten(element.ensightenEventWhenReady);
    }
  }

  private sendEventToEnsighten(ensightenEvent: EnsightenEventType) {
    const siteEvent = {};
    siteEvent[ensightenEvent] = EVENT_TRUE;
    try {
      if (typeof this._window.nativeWindow._trackAnalytics === 'function') {
          this._window.nativeWindow._trackAnalytics({
          site_events: siteEvent
        });
      } else {
        //  Ensighten Error: window._trackAnalytics is not a function
        //  this._log.error('TrackingJsonService.doViewPageStart()', 'Error: virtual_page_view_start');
              }
    } catch (e) {
      //  Ensighten code errors so it doesn't crash the page
      // this._log.error('TrackingJsonService.doViewPageStart()', 'Error: virtual_page_view_start', e);
    }

    return this._trackingJson;
  }


  private assignData(trackingJson: TrackingJson) {
    Object.assign(this._trackingJson, trackingJson);
    this._window.nativeWindow.trackingJson = this._trackingJson;

    return this._trackingJson;
  }

  private loadStaticData() {
    this.assignData({
      sessionId: this.getOrCreateAppUUID(),
      eventId: String(AppEnsightenEventType.INITIAL),
      timestamp: String(Date.now()),
      actualTaxForeign: '',
      actualTaxUSD: '',
      currencyCodeUSD: 'USD',
      customerID: '',
      destLatitude: '',
      destLongitude: '',
      groupCode: '',
      hotelRank: '',
      initiatedActualTax: '0',
      initiatedActualUnitPrice: '0',
      initiatedAverageCashUsed: '0',
      initiatedAveragePoints: '0',
      initiatedAveragePointsUsed: '0',
      initiatedBookingTotal: '0',
      initiatedBookingType: 'C',
      initiatedCashUsed: '0',
      initiatedCurrencyCode: '',
      initiatedPointsUsed: '0',
      initiatedTax: '0',
      initiatedTotalPointsRequired: '0',
      isAMEA: false,
      isAMER: true,
      isEUR: false,
      orientation: this._mediaQueryService.orientation,
      pointsCashSplit: '',
      searchResults: '',
      searchedDestination: '',
      triggerTracking: false,
      viewport: this._mediaQueryService.viewPort()
    } as ITrackingJson);
    this.assignData(
      this.addDataFromCookies()
    );
    this.assignData(
      this.addDataFromAppConfig()
    );
    this.assignData(
      this.addDataFromFeatureToggles()
    );
  }

  private addDataFromCookies() {
    let edwSellSource = 'WEBWB';
    if (this._cookieService.check('akamaiIsTablet') && this._cookieService.get('akamaiIsTablet') === 'true') {
      edwSellSource = 'WEBTB';
    }
    if (this._cookieService.check('akamaiIsWirelessDevice') && this._cookieService.get('akamaiIsWirelessDevice') === 'true') {
      edwSellSource = 'WEBMW';
    }
    const akamaiCountryCode = useDefault(this._cookieService.get('akamaiCountryCode'), DEFAULT_COUNTRY_CODE);
    const data: ITrackingJson = {
      akamaiCountryCode: akamaiCountryCode,
      akamaiRegion: getRegionfromCountryCode(akamaiCountryCode).akamaiRegion,
      akamaiSubRegion: getRegionfromCountryCode(akamaiCountryCode).akamaiSubRegion,
      edwSellSource: edwSellSource,
      glat: useDefault(this._cookieService.get('IHGglat'), ''),
      iata: useDefault(this._cookieService.get('iataCookie'), ''),
      loginType: useDefault(this._cookieService.get('uhf_userstate'), '').toLowerCase(),
      beFreeCookieCreationDate: this._cookieService.get('beFreeCookieCreationDate')
    };

    return data;
  }

  private addDataFromAppConfig() {
    const brandInfo = useDefault(this._appConfig.brand, {});
    const localeInfo = useDefault(this._appConfig.locale, {localeString: ApplicationLocale.en_US});
    const languageCode = useDefault(this._appConfig.language, 'en');
    const countryCode = useDefault(this._appConfig.country, 'us');
    const brandCode = useDefault(brandInfo.brandCode, '6c');
    const data: TrackingJson = {
      brandCode: brandCode,
      categoryID: [brandCode, countryCode, languageCode].join(','),
      language: languageCode.toLowerCase(),
      siteBrand: brandCode,
      siteCountry : countryCode.toUpperCase(),
      siteLanguage : languageCode.toLowerCase(),
      siteLocale : useDefault(localeInfo.localeString, ApplicationLocale.en_US).toLowerCase()
    };

    return data;
  }

  private addDataFromFeatureToggles() {
    const state: IAppState = this._ngRedux.getState();
    const featureFlags: FeatureFlags = useDefault(state.featureFlags, new FeatureFlags());

    let deconstructedData = {};
    for (const t of featureFlags.flags) {
      deconstructedData[t.name.toString()] = t.version;
    }
    const orderedData = {};
    Object.keys(deconstructedData).sort().forEach(function(key) {
      orderedData[key] = deconstructedData[key];
    });

    return {featureToggles: JSON.stringify(orderedData)};
  }

  private mapReservation(state: IAppState): ITrackingJson {
    const reservation = state.reservation;
    const data: ITrackingJson = {
      adultCount: useDefault(reservation.stay.numberOfAdults, '').toString(),
      adultRoomNights: (reservation.stay.numberOfAdults * reservation.stay.numberOfNights || 1).toString(),
      bookingConfirmationNumber: reservation.confirmationNumber,
      bookingTotal: useDefault(reservation.stay.totalPrice, '').toString(),
      checkInDate: new Date(reservation.stay.checkInDate).toLocaleString('en-US').split(',')[0],
      checkOutDate: new Date(reservation.stay.checkOutDate).toLocaleString('en-US').split(',')[0],
      childCount: useDefault(reservation.stay.numberOfChildren, '').toString(),
      childrenRoomNights: (reservation.stay.numberOfChildren || 0 * reservation.stay.numberOfNights || 0).toString(),
      confirmationNumber: reservation.confirmationNumber,
      corporateId: reservation.corporateId,
      originalSellDate: reservation.originalSellDate,
      roomCount: useDefault(reservation.stay.numberOfRooms, '').toString(),
      roomNights: (reservation.stay.numberOfNights || 1 * reservation.stay.numberOfRooms || 1).toString(),
      stayLength: useDefault(reservation.stay.numberOfNights, ''),
      userLastName: useDefault(reservation.guest.lastName, '').toUpperCase(),
      actualTax: reservation.rate.serviceCharges.length > 0 ? useDefault(reservation.rate.serviceCharges[0].amount, '').toString() : '',
      actualUnitPrice: useDefault(reservation.rate.charges.price, '').toString(),
      currencyCode: reservation.rate.currencyCode,
      currencyCodeForeign: reservation.room.currencyCode,
      unitPrice: useDefault(reservation.rate.charges.price, '').toString(),
      pointsUsed: useDefault(reservation.rate.totalPointsCost, '').toString(),
      rateCode: reservation.rate.code,
      roomCode: reservation.room.code,
      roomRevenueForeign: (reservation.stay.numberOfNights * reservation.stay.numberOfRooms * reservation.rate.charges.averageNightlyRate).toString(),
      roomRevenueUSD: (reservation.stay.numberOfNights * reservation.stay.numberOfRooms * reservation.rate.charges.averageNightlyRate).toString(),
      tax: useDefault(reservation.rate.charges.taxes, '').toString(),
      unitPriceForeign: useDefault(reservation.rate.charges.averageNightlyRate, '').toString(),
      unitPriceUSD: useDefault(reservation.rate.charges.averageNightlyRate, '').toString(),
    };
    return data;
  }
  private mapHotel(state: IAppState): ITrackingJson {
    const hotel = state.hotel;
    const data: ITrackingJson = {
      bookedHotelBrand: hotel.brandCode,
      bookedLocation: [
        hotel.address.city,
        hotel.address.stateCode,
        hotel.address.countryName ].join(','),
      city: hotel.address.city,
      country: useDefault(hotel.address.countryCode, '').toLowerCase(),
      countryBooked: hotel.address.countryName,
      hotelBrand: hotel.chainCode,
      hotelBrand4Digit: hotel.brandCode,
      hotelCityStateCountryCode: [
        hotel.address.city,
        hotel.address.stateCode,
        hotel.address.countryName ].join(','),
      hotelLocation: [
        hotel.address.city,
        hotel.address.stateCode,
        hotel.address.countryName ].join(','),
      propertyCode: hotel.mnemonic,
      propertyLat: useDefault(hotel.latitude, '').toString(),
      propertyLong: useDefault(hotel.longitude, '').toString(),
      propertyName: hotel.name,
      state: hotel.address.stateCode
     };
    return data;
  };

  private mapMember(state: IAppState): ITrackingJson {
    const member = state.member;
    const data: ITrackingJson = {
      isEmployee: member.isEmployee
    };
    return data;
  }

  private getOrCreateAppUUID() {
    let trackingSession = new TrackingSession();
    try {
      const trackingSessionData = this._sessionStorageService.retrieve(APP_TRACKING_UUID_NAME);
      if (!trackingSessionData) {
        this._log.info('Creating new TrackingSession', CLASS_NAME);
        const sessionId = UUID.UUID();
        trackingSession = new TrackingSession({sessionId});
        this._sessionStorageService.store(APP_TRACKING_UUID_NAME, trackingSession);
      } else {
        this._log.info('Retrieving TrackingSession', CLASS_NAME);
        trackingSession = new TrackingSession(trackingSessionData);
      }
    } catch (error) {
      this._log.error('Error while get/update TrackingSession: ' , CLASS_NAME, error);
    }
    return trackingSession.sessionId;
  }

}



// WEBPACK FOOTER //
// ./src/main/webapp/stay-mgmt/app/api/services/trackingJson.service.ts